import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Button, ListTable, Notice, Plate, TabPanel, Tabs, type ListColumn } from "@wilco/ui";
import { useState } from "react";
import { t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { terminalsApi, type Session, type Terminal } from "./api.ts";
import { BackToHr, DayField, useNames } from "./common.tsx";
import { lineQuery, PEOPLE, sessionsQuery, terminalsQuery, workstationsQuery } from "./queries.ts";
import { hm, localDate, localTime, minutesBetween } from "./time.ts";

export type TerminalsTab = "devices" | "sessions";

const route = getRouteApi("/app/hr/terminals");

/**
 * «Термінали» (PRS): терминалы ZKTeco на рабочих местах по ADMS и время
 * на местах из касаний. Терминал заводится сам рукопожатием и выключен,
 * пока его не включат здесь.
 */
export function TerminalsPage() {
  const { tab = "devices", date } = route.useSearch();
  const navigate = useNavigate();
  const day = date ?? localDate();
  const go = (s: { tab?: TerminalsTab; date?: string }) => void navigate({ to: "/hr/terminals", search: { tab, date, ...s }, replace: true });
  return (
    <div className="page people">
      <div className="page-head">
        <BackToHr />
        <h1 className="page-title">{t("term.title")}</h1>
      </div>
      <Tabs
        label={t("term.title")}
        value={tab}
        onValueChange={(v) => go({ tab: v })}
        items={[
          { value: "devices", label: t("term.tab.devices") },
          { value: "sessions", label: t("term.tab.sessions") },
        ]}
      >
        <TabPanel value="devices">
          <DevicesTab />
        </TabPanel>
        <TabPanel value="sessions">
          <SessionsTab day={day} onDay={(d) => go({ date: d })} />
        </TabPanel>
      </Tabs>
    </div>
  );
}

function useWorkstationName(): (id: string | null | undefined) => string {
  const ws = useQuery(workstationsQuery);
  return (id) => (id ? (ws.data?.find((w) => w.id === id)?.name ?? "—") : "—");
}

const seen = (iso: string | null | undefined) => (iso ? `${localDate(Date.parse(iso)).slice(5).split("-").reverse().join(".")} ${localTime(iso)}` : "—");

function DevicesTab() {
  const devices = useQuery(terminalsQuery);
  const ws = useWorkstationName();
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const done = (title: string) => ({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PEOPLE });
      setNotice({ tone: "success", title });
    },
    onError: (err: unknown) => setNotice(errorNotice(err)),
  });
  const toggle = useMutation({ mutationFn: (d: Terminal) => terminalsApi.update(d.id, !d.enabled, d.name, d.workstationId ?? null) });
  const sync = useMutation({ mutationFn: (d: Terminal) => terminalsApi.sync(d.id) });
  return (
    <div className="people-tab">
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {devices.error ? <Notice tone="danger" title={errorNotice(devices.error).title} className="stock-notice" /> : null}
      {devices.data?.length === 0 ? <p className="w-muted people-empty">{t("term.empty")}</p> : null}
      <ul className="people-cards">
        {devices.data?.map((d) => (
          <li key={d.id}>
            <Plate cut={false} className="people-device">
              <div className="people-device-head">
                <span className="people-list-main">
                  <span className="people-group-name">{d.name}</span>
                  <span className="w-muted w-num">
                    {d.serial} · {d.readerType === "card" ? t("term.type.card") : t("term.type.zkteco")}
                  </span>
                </span>
                <span className="tags">
                  {d.enabled ? (
                    d.online ? (
                      <Badge tone="success">{t("term.online")}</Badge>
                    ) : (
                      <Badge tone="danger">{t("term.offline")}</Badge>
                    )
                  ) : (
                    <Badge>{t("term.disabled")}</Badge>
                  )}
                </span>
              </div>
              <dl className="people-facts">
                <dt>{t("term.workstation")}</dt>
                <dd>{ws(d.workstationId)}</dd>
                <dt>{t("term.lastSeen")}</dt>
                <dd className="w-num">{seen(d.lastSeen)}</dd>
                <dt>{t("term.skew")}</dt>
                <dd className="w-num">
                  {d.clockSkewMinutes === null || d.clockSkewMinutes === undefined ? "—" : d.clockSkewMinutes === 0 ? "0" : <Badge tone="warning">{String(d.clockSkewMinutes)}</Badge>}
                </dd>
                <dt>{t("term.pending")}</dt>
                <dd className="w-num">{String(d.pendingCommands)}</dd>
              </dl>
              <div className="order-actions">
                <Button variant={d.enabled ? "ghost" : "primary"} disabled={toggle.isPending} onClick={() => toggle.mutate(d, done(d.enabled ? t("term.disabledDone") : t("term.enabledDone")))}>
                  {d.enabled ? t("term.disable") : t("term.enable")}
                </Button>
                {d.readerType === "card" ? null : (
                  <Button disabled={sync.isPending || !d.enabled} onClick={() => sync.mutate(d, done(t("term.synced")))}>
                    {t("term.sync")}
                  </Button>
                )}
              </div>
            </Plate>
          </li>
        ))}
      </ul>
    </div>
  );
}

const kindKey = (k: string) => (k === "lunch" || k === "break" ? k : "work");
const closedKey = (c: string) => (["tap", "switch", "handover", "auto"].includes(c) ? (c as "tap" | "switch" | "handover" | "auto") : "tap");

/** «Час на робочих місцях» за день и «Лінія зараз»: кто где работает сейчас. */
function SessionsTab({ day, onDay }: { day: string; onDay: (d: string) => void }) {
  const sessions = useQuery(sessionsQuery(day));
  const line = useQuery(lineQuery);
  const name = useNames();
  const ws = useWorkstationName();
  const columns: ListColumn<Session>[] = [
    { key: "who", header: t("att.employee"), kind: "title", render: (s) => name(s.employeeId) },
    { key: "ws", header: t("term.workstation"), kind: "text", width: "10rem", render: (s) => ws(s.workstationId) },
    { key: "kind", header: t("term.kind"), kind: "text", width: "8rem", render: (s) => <Badge tone={s.kind === "work" ? undefined : "info"}>{t(`term.kind.${kindKey(s.kind)}`)}</Badge> },
    { key: "time", header: t("term.time"), kind: "text", width: "8rem", render: (s) => <span className="w-num">{`${localTime(s.start)} – ${s.stop ? localTime(s.stop) : "…"}`}</span> },
    {
      key: "closed",
      header: t("term.closedBy"),
      kind: "text",
      width: "10rem",
      render: (s) => (s.closedBy ? <span className={s.closedBy === "auto" ? "people-warn" : undefined}>{t(`term.closed.${closedKey(s.closedBy)}`)}</span> : <Badge tone="warning">{t("att.open")}</Badge>),
    },
    { key: "len", header: t("term.length"), kind: "number", width: "6rem", render: (s) => (s.stop ? hm(minutesBetween(s.start, s.stop)) : "—") },
  ];
  return (
    <div className="people-tab">
      {day === localDate() && line.data && line.data.length > 0 ? (
        <>
          <h2 className="people-h2">{t("term.line")}</h2>
          <ul className="people-line">
            {line.data.map((l) => (
              <li key={l.workstationId} className="people-line-spot" data-busy={l.employeeId ? "" : undefined}>
                <span className="people-line-ws">{l.name}</span>
                <span>{l.employeeId ? name(l.employeeId) : t("term.free")}</span>
                <span className="w-muted">
                  {l.employeeId && l.since ? t("term.since", { time: localTime(l.since) }) : l.leftEmployeeId && l.leftAt ? t("term.left", { name: name(l.leftEmployeeId), time: localTime(l.leftAt) }) : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <DayField value={day} onChange={onDay} />
      {sessions.error ? <Notice tone="danger" title={errorNotice(sessions.error).title} className="stock-notice" /> : null}
      <ListTable
        label={t("term.tab.sessions")}
        columns={columns}
        rows={sessions.data ?? []}
        rowKey={(s) => `${s.employeeId}-${s.start}`}
        empty={sessions.data ? t("term.noSessions") : null}
      />
    </div>
  );
}
